// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Filter, ArrowDownWideNarrow, X } from 'lucide-react';
import {
  Button,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';

import { NWTextInput } from 'Components/shared';

const StyledFilterOrder = styled.div`
  .mobileToolbar {
    display: none;
    gap: 1em;
    margin-bottom: 18px;

    &.fixed {
      display: none;
      justify-content: center;
      align-items: center;
      padding: 15px;
      margin: 0;
      width: 100vw;
      height: 80px;
      position: fixed;
      bottom: 0;
      left: 0;
      transition: transform 0.2s ease;
      overflow: visible;
      opacity: ${ props => props.isVisible ? 1 : 0 };
      visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
      background-color: ${ props => props.theme.colors.white };
      box-shadow: 0 3px 6px #00000029;
      z-index: 8999;
    }

    button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 8px;
      background: #E7ECF2;
      border: 1px solid #B2C2D5;
      border-radius: 10px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        svg {
          margin-right: 8px;
        }
      }

      .info {
        color: #707070;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .backDrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: #000000b3;
    display: ${ props => props.mobileSortOpen ? 'flex' : 'none' };
    align-items: flex-end;

    .content {
      flex: 1;
      padding: 15px;
      background: #fff;

      .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;

        h3 {
          font-size: 20px;
          line-height: 27px;
        }

        button {
          background: transparent;
          border: none;
          cursor: pointer;
        }
      }

      .cds--radio-button-group {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .cds--radio-button-wrapper {
          margin: 12px 0;
        }
      }
    }
  }

  .filterSection {
    display: flex;
    padding: 10px 0;

    .mobileHeader {
      display: none;

      .mobileTitleSection {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px 15px 15px;

        h2 {
          font-size: 20px;
        }

        button {
          cursor: pointer;
          color: #D52B1E;
          background: transparent;
          border: none;
        }
      }

      .activeFilters {
        display: flex;
        background: #F3F2F2;
        padding: 15px;
        flex-direction: column;

        .cds--form-item {
          margin-bottom: 0;
        }

        > span {
          font-weight: 600;
        }

        .activeFilterList {
          padding: 10px 0;
          display: flex;
          text-wrap: nowrap;
          overflow-x: scroll;

          .filter { 
            margin-right: 10px;
          }
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;

          svg {
            stroke: #D52B1E;
          }
        }
      }
    }

    .actionsSection {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      flex: 1;

      .reservedProductsFilters {
        display: flex;
        gap: 10px;

        .cds--text-input-wrapper {
          margin-bottom: 0;
        }

        > div:first-of-type {
          margin-bottom: 0;
        }

        input[type="text"] {
          min-width: 360px;
        }
      }

      .actions {
        display: flex;
        gap: 10px;

        button.cds--btn {
          height: 40px;
          max-width: none;
          min-width: 170px;
          -webkit-box-pack: center;
          justify-content: center;
          font-size: 1rem;
          text-align: center;

          &.deleteFilters {
            border: 1px solid #D52B1E;
            color: #D52B1E;

            &:hover {
              background: transparent;
            }
          }
        }
      }
    }

    .cds--label {
      display: none;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .filterSection {
      display: ${ props => props.mobileFiltersOpen ? 'flex' : 'none' };
      flex-direction: column;
      padding: 25px 0;
      gap: 20px;
      position: fixed;
      inset: 0;
      background: #fff;
      z-index: 9001;
      margin-top: 0;
      overflow-y: scroll;

      .cds--label {
        display: inline-block;
        font-size: 15px;
        font-weight: 600;
        color: #3a3a3a;
      }

      .mobileHeader {
        display: block;
      }

      .actionsSection {
        gap: 20px;
        flex-direction: column;
        padding: 15px;

        .reservedProductsFilters {
          flex-direction: column;
          width: 100%;
        }

        .actions {
          width: 100%;

          button.cds--btn {
            min-width: 100%;

            &.deleteFilters {
              display: none;
            }
          }
        }
      }
    }

    .mobileToolbar {
      display: flex;

      &.fixed {
        display: flex;
      }
    }
  }
`;

const FilterReservedProducts = ({
  register,
  onSubmit,
  onClearFilters,
  onClearFilter,
  setMobileFiltersOpen,
  mobileFiltersOpen,
  setMobileSortOpen,
  handleMobileOrderChange,
  activeFilters,
  currentSort,
  sortOptions,
  mobileSortOpen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const filterKeys = useMemo(() => [
    { key: 'order_number', label: t('orders.orderNumber') },
    { key: 'product_code', label: t('orders.productCode') },
  ], []);

  const renderFilter = ({ key, value }) => {
    const filterName = filterKeys.find(item => item.key === key)?.label;
    let filterValue = value;

    if (key === 'drop_shipment') {
      filterValue = value ? t('common.yes') : t('common.no');
    }

    return (
      <div>
        <span>{ `${ filterName }: ${ filterValue }` }</span>
        <button type="button" onClick={ () => onClearFilter(key) }><X /></button>
      </div>
    );
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    ...register(fieldName),
  });

  return (
    <StyledFilterOrder theme={ theme } mobileSortOpen={ mobileSortOpen } mobileFiltersOpen={ mobileFiltersOpen }>
      <div className="mobileToolbar">
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ Object.keys(activeFilters).length } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </div>
      <div className="mobileSort">
        <div className="backDrop">
          <div className="content">
            <div className="header">
              <h3>{ t('products.sortBy') }</h3>
              <button type="button" onClick={ () => setMobileSortOpen(false) }>
                <X />
              </button>
            </div>
            <RadioButtonGroup
              name="sortReservedProducts"
              valueSelected={ currentSort?.id }
              onChange={ handleMobileOrderChange }
            >
              { sortOptions.map(opt => (
                <RadioButton key={ opt.id } labelText={ opt.label } value={ opt.id } id={ opt.id } />
              )) }
            </RadioButtonGroup>
          </div>
        </div>
      </div>
      <div className="filterSection">
        <div className="mobileHeader">
          <div className="mobileTitleSection">
            <h2>{ t('orders.filters') }</h2>
            <button
              type="button"
              onClick={ () => {
                onClearFilters();
                setMobileFiltersOpen(false);
              } }
            >
              { t('products.clearAllFilters')}
            </button>
          </div>
          { Object.keys(activeFilters).length > 0 && (
            <div className="activeFilters">
              <span>{ `${ Object.keys(activeFilters).length } ${ t('orders.activeFilters') }` }</span>
              <div className="activeFilterList">
                { Object.entries(activeFilters).map(([key, value]) => <div key={ key } className="filter">{ renderFilter({ key, value }) }</div>) }
              </div>
            </div>
          )}
        </div>
        <div className="actionsSection">
          <div className="reservedProductsFilters">
            <NWTextInput
              id="search"
              labelText={ t('orders.orderNumber') }
              placeholder={ t('orders.orderNumber') }
              size="md"
              { ...sharedProps('order_number') }
            />
            <NWTextInput
              id="search"
              labelText={ t('orders.productCode') }
              placeholder={ t('orders.productCode') }
              size="md"
              { ...sharedProps('product_code') }
            />
          </div>
          <div className="actions">
            <Button
              className="filterOrders"
              kind="danger"
              onClick={ onSubmit }
            >
              { t('orders.filter') }
            </Button>
            { !!Object.keys(activeFilters).length && (
              <Button
                className="deleteFilters"
                kind="danger--ghost"
                onClick={ onClearFilters }
              >
                { t('order.deleteFilters') }
              </Button>
            )}
          </div>
        </div>
      </div>
    </StyledFilterOrder>
  );
};

FilterReservedProducts.propTypes = {
  register: func,
  onSubmit: func,
  onClearFilters: func,
  onClearFilter: func,
  setMobileFiltersOpen: func,
  setMobileSortOpen: func,
  handleMobileOrderChange: func,
  activeFilters: object,
  currentSort: object,
  mobileFiltersOpen: bool,
  sortOptions: array,
  mobileSortOpen: bool,
};

FilterReservedProducts.displayName = 'FilterReservedProductsComponent';

export default FilterReservedProducts;
